import React from 'react'
import { Link } from '@inertiajs/react'
import { MoonStar, ArrowRight } from 'lucide-react'

export interface ClearanceMorningAfterData {
  /** The night this line describes, in the property's own timezone. */
  date: string
  /** How many rooms cleared — families, counted once however often a night froze and re-armed. */
  rooms: number
  /** Arrivals booked while the night was cleared. An association, never a claim of causation. */
  sold: number
  /** Per-night gross of those arrivals; null when no booking yields an honest amount. */
  amount: number | null
  /** The whole line, composed by the backend. Rendered verbatim — this page does no arithmetic. */
  sentence: string
}

interface ClearanceMorningAfterProps {
  line: ClearanceMorningAfterData | null | undefined
  propertyBasePath: string
}

/**
 * WHAT CLEARANCE DID WHILE THE OPERATOR WAS ASLEEP — one line, and only one
 * (docs/plans/2026-08-13-last-minute-clearance.md §7; §3.11 parks everything larger).
 *
 * DELIBERATELY QUIET, AND DELIBERATELY NOT A BANNER. The layout's warning rail means a fault — a
 * failed push, a dead PMS connection — and a rule doing exactly what the operator switched it on
 * to do is not one. Same locked reasoning as the Target verdicts, which are barred from that rail
 * for the same reason.
 *
 * ZERO MATH HERE. The sentence, the counts and the money are all composed and computed by
 * ResolveClearanceMorningAfterMutation; this component decides where the words sit and nothing
 * else. It renders nothing at all unless the rule is on and a room actually cleared last night.
 */
export default function ClearanceMorningAfter({ line, propertyBasePath }: ClearanceMorningAfterProps) {
  if (!line) return null

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-amber-200/70 bg-amber-50/60 px-4 py-2.5">
      <MoonStar className="h-3.5 w-3.5 shrink-0 text-amber-600" />
      <p className="min-w-0 flex-1 text-xs font-medium text-foreground">{line.sentence}</p>
      <Link
        href={`${propertyBasePath}/pricing-rules/clearance`}
        className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[11px] font-semibold text-amber-800 transition-colors hover:text-amber-900"
      >
        Clearance
        <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  )
}
